import {
  Component,
  forwardRef,
  Output,
  EventEmitter,
  OnInit,
  HostListener,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import {
  COUNTRIES,
  Country,
  InputMode,
  AllowedInputType,
} from './smart-input.types';

@Component({
  selector: 'app-smart-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './smart-input.component.html',
  styleUrls: ['./smart-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SmartInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SmartInputComponent),
      multi: true,
    },
  ],
})
export class SmartInputComponent
  implements ControlValueAccessor, Validator, OnInit
{
  @Output() valueChange = new EventEmitter<string>();
  @Output() modeChange = new EventEmitter<InputMode>();

  @Input() allowedTypes: AllowedInputType = 'both';

  inputValue = '';
  inputMode: InputMode = 'undetermined';
  selectedCountry: Country;
  isDropdownOpen = false;
  searchTerm = '';
  isTouched = false;
  isDisabled = false;

  countries: Country[] = COUNTRIES;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.selectedCountry =
      this.countries.find((country) => country.code === 'DO') ??
      this.countries[0];
  }

  ngOnInit(): void {
    if (this.allowedTypes === 'email') {
      this.inputMode = 'email';
    } else if (this.allowedTypes === 'phone') {
      this.inputMode = 'phone';
    }
  }

  writeValue(value: string): void {
    this.inputValue = value || '';
    this.detectInputMode(this.inputValue);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.inputValue) {
      return null;
    }

    if (this.inputMode === 'email') {
      return this.validateEmail() ? null : { invalidEmail: true };
    }

    if (this.inputMode === 'phone') {
      return this.validatePhone() ? null : { invalidPhone: true };
    }

    return null;
  }

  // Input change handler
  onInputChange(event: any): void {
    let value = event.target.value;

    if (this.inputMode === 'phone') {
      const formatted = this.formatPhoneNumber(value);
      this.inputValue = formatted;

      // 👇 Forzar que el input muestre el valor formateado (truncado)
      if (formatted !== value) {
        event.target.value = formatted;
      }
    } else {
      this.inputValue = value;
    }

    this.detectInputMode(value);

    // Emitir el valor completo
    const fullValue = this.getFullValue();
    this.onChange(fullValue);
    this.valueChange.emit(fullValue);
  }

  onInputBlur(): void {
    if (!this.isTouched) {
      this.isTouched = true;
      this.onTouched();
    }
  }

  // Detecta si el input es un correo o un número de teléfono
  private detectInputMode(value: string): void {
    const previousMode = this.inputMode;

    if (this.allowedTypes === 'email') {
      this.inputMode = 'email';
    } else if (this.allowedTypes === 'phone') {
      this.inputMode = 'phone';
    } else {
      if (!value || value.trim() === '') {
        this.inputMode = 'undetermined';
      } else if (value.includes('@')) {
        // Si contiene @, es un correo
        this.inputMode = 'email';
      } else {
        // Comprueba si es numérico (permitiendo +, espacios, guiones, paréntesis)
        const numericPattern = /^[\d\s\-\+\(\)]+$/;
        const digitsOnly = value.replace(/[\s\-\+\(\)]/g, '');

        // Mínimo 6 dígitos para considerar un número de teléfono
        if (numericPattern.test(value) && digitsOnly.length >= 6) {
          this.inputMode = 'phone';
        } else if (digitsOnly.length > 0 && digitsOnly.length < 6) {
          this.inputMode = 'undetermined';
        } else {
          this.inputMode = 'email';
        }
      }
    }

    if (previousMode !== this.inputMode) {
      this.modeChange.emit(this.inputMode);
    }
  }

  // Obtiene el valor completo (con el código de país para el número de teléfono)
  private getFullValue(): string {
    if (this.inputMode === 'phone') {
      const cleanNumber = this.inputValue.replace(/[\s\-\(\)]/g, '');
      // Si el número no empieza con +, agrega el código de país
      if (!cleanNumber.startsWith('+')) {
        return `${this.selectedCountry.dialCode}${cleanNumber}`;
      }
      return cleanNumber;
    }
    return this.inputValue;
  }

  private formatPhoneNumber(value: string): string {
    if (!this.selectedCountry.format) {
      return value;
    }

    const cleaned = value.replace(/\D/g, '');

    // Limitar longitud según el país
    const maxLength = this.selectedCountry.maxLength || 15;
    const truncated = cleaned.slice(0, maxLength);

    let formatted = '';
    let digitIndex = 0;

    for (
      let i = 0;
      i < this.selectedCountry.format.length && digitIndex < truncated.length;
      i++
    ) {
      const char = this.selectedCountry.format[i];
      if (char === 'X') {
        formatted += truncated[digitIndex];
        digitIndex++;
      } else {
        if (digitIndex < truncated.length) {
          formatted += char;
        }
      }
    }

    return formatted;
  }

  private validateEmail(): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(this.inputValue);
  }

  private validatePhone(): boolean {
    const digitsOnly = this.inputValue.replace(/[\s\-\+\(\)]/g, '');
    if (this.selectedCountry.maxLength) {
      return digitsOnly.length === this.selectedCountry.maxLength;
    }
    return digitsOnly.length >= 6 && digitsOnly.length <= 15;
  }

  get isInvalid(): boolean {
    if (!this.isTouched || !this.inputValue) {
      return false;
    }

    if (this.inputMode === 'email') {
      return !this.validateEmail();
    }

    if (this.inputMode === 'phone') {
      return !this.validatePhone();
    }

    return false;
  }

  get errorMessage(): string {
    if (this.inputMode === 'email') {
      return 'Este correo no es válido';
    }
    if (this.inputMode === 'phone') {
      return 'Este número no es válido';
    }
    return '';
  }

  get placeholder(): string {
    if (this.allowedTypes === 'email') {
      return 'james007@gmail.com';
    }
    if (this.inputMode === 'phone' && this.selectedCountry.format) {
      return this.selectedCountry.format.replace(/X/g, '9');
    }
    return 'james007@gmail.com o 8091234567';
  }

  get inputType(): string {
    return this.inputMode === 'phone' ? 'tel' : 'text';
  }

  toggleDropdown(): void {
    if (!this.isDisabled && this.inputMode === 'phone') {
      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.isDropdownOpen) {
        this.searchTerm = '';
      }
    }
  }

  selectCountry(country: Country): void {
    this.selectedCountry = country;
    this.isDropdownOpen = false;
    this.searchTerm = '';

    const fullValue = this.getFullValue();
    this.onChange(fullValue);
    this.valueChange.emit(fullValue);
  }

  get filteredCountries(): Country[] {
    if (!this.searchTerm) {
      return this.countries;
    }

    const search = this.searchTerm.toLowerCase();
    return this.countries.filter(
      (country) =>
        country.name.toLowerCase().includes(search) ||
        country.dialCode.includes(search) ||
        country.code.toLowerCase().includes(search)
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.country-selector')) {
      this.closeDropdown();
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.searchTerm = '';
  }
}
